import React, { Component } from "react";
import FullPageWrapper from "../../wrappers/FullPageWrapper";
import { createRefPath } from "global/firestoreData/appData/appRefPaths";
import { buildFcmPayload } from "global/functions/fbNotifications";
import { _developerFcm } from "project/appConfiguration";
import { _fcmTopicKeys } from "global/cnr/contexts/useHelpers/useFcm";

class ErrorBoundary extends Component {
  state = {
    hasError: false,
    message: null,
    stack: null,
    componentStack: null,
  };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {

    const { parentContext } = this.props; // ✅ passed from wrapper  

    const message = error?.message || "Unknown error";
    const stack = error?.stack || "";
    const componentStack = errorInfo?.componentStack || "";

    // Log clearly in console
    console.group("🔥 ErrorBoundary caught an error");
    console.error("Message:", message);
    console.error("Stack:", stack);
    console.error("Component Stack:", componentStack);
    console.groupEnd();

    // ✅ Get just the first non-empty line of the stack
    const stackLines = stack
      .split("\n")
      .map(line => line.trim())
      .filter(Boolean);

    const firstStackLine = stackLines[0] || "No stack info";
    const secondStackLine = stackLines[1] || "No second stack line";

    try {
      const { states, fns, fn_cls } = parentContext ?? {}
      const { paps_state } = states ?? {}
      const { database_fns } = fns ?? {}
      const { pathViews } = paps_state ?? {}
      const refPath = createRefPath(['_errorBoundry'])
      const dataToUpdate = {
        firstStackLine,
        secondStackLine,
        message,
        pathViews,
        timestamp: new Date().toISOString()
      }
      database_fns.add_doc(refPath, dataToUpdate)

      const sendFcm = () => {
        const s = buildFcmPayload(null, _fcmTopicKeys.error, 'App Error', 'Error: ' + message + '. Triggered in: ' + firstStackLine + '/' + secondStackLine)
        fn_cls.sendFcmPayload(s)
      }

      if (process.env.NODE_ENV !== 'development') {
        sendFcm()
      }

    } catch (error) {
      // nothing
    }

    // Update state to show UI
    this.setState({ message, stack, componentStack });
  }

  renderStackTrace() {
    const { componentStack } = this.state;
    if (!componentStack) return null;

    const lines = componentStack
      .split("\n")
      .filter(Boolean)
      .slice(0, 5);

    return (
      <ul>
        {lines.map((line, index) => (
          <li key={index} style={{ wordBreak: "break-word" }}>{line.trim()}</li>
        ))}
      </ul>
    );
  }

  render() {
    const { hasError, message } = this.state;
    const { navigate, children, handleCancel } = this.props;

    if (hasError) {
      return (
        <FullPageWrapper
          content={
            <div
              className="stack-error"
              style={{ overflowY: "auto", height: "100%", padding: "1rem" }}
            >
              <h3>Error details</h3>
              <p>{message}</p>
              <h4>Component Stack</h4>
              {this.renderStackTrace()}
            </div>
          }
          handleCancel={handleCancel}
          topperCaption="Oops, something went wrong"
          topperCaption2={message}
        />
      );
    }

    return children;
  }
}

export default ErrorBoundary;
